import * as React from "react";
import { Modal, Stack, Text, PrimaryButton, DefaultButton, Checkbox, Label } from "@fluentui/react";
import { useId } from "@fluentui/react-hooks";
import { ILanguageInfo } from "../../services/SettingsService";
import { useEffect, useState } from "react";
import { ISettingsData, saveSettingsDialog, closeSettingsDialog, loadLanguageList } from "./settingsSlice";
import * as globalConfigActions from "../../actions/globalConfigActions";
import { Utils } from "../../Utils";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useSelectStyles } from "../../hooks";
import { applyTheme, isThemeName, ThemeName } from "../../services/ThemeService";

/**
 * Finds a language info object by a language id
 *
 * @param languages - The language info list
 * @param languageId - The language id to look for
 * @returns the matching LanguageInfo
 */
const findLanguageMatch = (languages: ILanguageInfo[], languageId: string): ILanguageInfo | undefined => {
  return (
    languages.filter((l) => l.ietfLanguageTag === languageId)[0] ||
    languages.filter((l) => languageId.split("-")[0] === l.ietfLanguageTag)[0]
  );
};

/**
 * Interface for settings form props
 */
interface ISettingsFormProps {
  /**
   * The languages
   */
  languages: ILanguageInfo[];

  /**
   * The settings data
   */
  settings: ISettingsData;

  /**
   * Fired when settings change
   */
  onSettingsChanged: (settings: ISettingsData) => void;
}

/**
 * The settings form component
 *
 * @param props - the component properties
 * @returns - the markup
 */
const SettingsForm: React.FC<ISettingsFormProps> = ({ languages, settings, onSettingsChanged }) => {
  const [confirmAutoCompleteVisible, setConfirmAutoCompleteVisible] = useState(false);

  const currentLangInfo = findLanguageMatch(languages, settings.language);

  const langSelectId = useId("langselect");
  const themeSelectId = useId("themeselect");

  const selectStyles = useSelectStyles();

  const themeOptions = [
    { key: "classic", text: ClientStrings.SettingsThemeClassic || "Classic" },
    { key: "light", text: ClientStrings.SettingsThemeLight || "Light" },
    { key: "dark", text: ClientStrings.SettingsThemeDark || "Dark" },
    { key: "system", text: ClientStrings.SettingsThemeSystem || "System" },
  ];

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    onSettingsChanged({
      ...settings,
      language: e.target.value,
      isRtl: findLanguageMatch(languages, e.target.value)?.isRightToLeft ?? false,
    });
  };

  const handleThemeChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    if (isThemeName(e.target.value)) {
      onSettingsChanged({
        ...settings,
        themeName: e.target.value,
      });
    }
  };

  return (
    <form>
      <Stack tokens={{ childrenGap: 12 }}>
        <Label htmlFor={langSelectId}>{ClientStrings.SettingsLanguageLabel}</Label>
        <select
          id={langSelectId}
          className={selectStyles}
          value={currentLangInfo?.ietfLanguageTag}
          onBlur={handleChange}
          onChange={handleChange}
        >
          {languages.map((lang) => {
            const isSelected = lang.ietfLanguageTag === currentLangInfo?.ietfLanguageTag;
            return (
              <option key={lang.ietfLanguageTag} value={lang.ietfLanguageTag} aria-selected={isSelected}>
                {lang.nativeName === lang.displayName ? lang.nativeName : `${lang.nativeName} (${lang.displayName})`}
              </option>
            );
          })}
        </select>
        <Checkbox
          checked={settings.autoFillEnabled}
          label={ClientStrings.SettingsEnableAutofillLabel}
          onChange={(_e, enabled): void => {
            if (enabled) {
              setConfirmAutoCompleteVisible(true);
            } else {
              onSettingsChanged({
                ...settings,
                autoFillEnabled: false,
              });
            }
          }}
        />
        <ConfirmDialog
          visible={confirmAutoCompleteVisible}
          message={`${ClientStrings.SettingsEnableAutofillWarning} ${ClientStrings.SettingsEnableAutofillContinue}`}
          onContinue={(): void => {
            setConfirmAutoCompleteVisible(false);
            onSettingsChanged({ ...settings, autoFillEnabled: true });
          }}
          onCancel={(): void => {
            setConfirmAutoCompleteVisible(false);
            onSettingsChanged({ ...settings, autoFillEnabled: false });
          }}
        />
        <Label htmlFor={themeSelectId}>{ClientStrings.SettingsThemeLabel ?? "Theme"}</Label>
        <select
          id={themeSelectId}
          className={selectStyles}
          value={settings.themeName}
          onBlur={handleThemeChange}
          onChange={handleThemeChange}
        >
          {themeOptions.map((theme) => {
            const isSelected = theme.key === settings.themeName;
            return (
              <option key={theme.key} value={theme.key} aria-selected={isSelected}>
                {theme.text}
              </option>
            );
          })}
        </select>
      </Stack>
    </form>
  );
};

/**
 * The Settings Dialog component
 *
 * @returns the markup
 */
export const SettingsDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const visible = useAppSelector((state) => state.settings.showDialog);
  const currentLanguage = useAppSelector((state) => state.globalConfig.language);
  const autoFillEnabled = useAppSelector((state) => state.globalConfig.autoFillEnabled);
  const themeName = useAppSelector((state) => state.globalConfig.themeName);
  const [languages, setLanguages] = useState<ILanguageInfo[]>([]);

  const currentLangInfo = findLanguageMatch(languages, currentLanguage);

  const [settings, setSettings] = useState<ISettingsData>({
    language: currentLangInfo?.ietfLanguageTag ?? currentLanguage,
    isRtl: currentLangInfo?.isRightToLeft ?? false,
    autoFillEnabled,
    themeName,
  });
  const [savedThemeName, setSavedThemeName] = useState<ThemeName>(themeName);

  useEffect(() => {
    const loadLanguages = async () => {
      const result = await dispatch(loadLanguageList(currentLanguage));
      if (loadLanguageList.fulfilled.match(result)) {
        setLanguages(result.payload);
      }
    };

    void loadLanguages();
  }, [dispatch, currentLanguage]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    setSavedThemeName(themeName);
    setSettings({
      language: currentLangInfo?.ietfLanguageTag ?? currentLanguage,
      isRtl: currentLangInfo?.isRightToLeft ?? false,
      autoFillEnabled,
      themeName,
    });
  }, [visible]);

  const onSettingsChanged = (updatedSettings: ISettingsData): void => {
    setSettings((previousSettings) => {
      if (updatedSettings.themeName !== previousSettings.themeName) {
        dispatch(globalConfigActions.setThemeName(updatedSettings.themeName));
        applyTheme(updatedSettings.themeName);
      }

      return updatedSettings;
    });
  };

  /**
   * Fired when discard button is clicked
   */
  const onDiscard = () => {
    dispatch(globalConfigActions.setThemeName(savedThemeName));
    applyTheme(savedThemeName);
    dispatch(closeSettingsDialog());
    setSettings({
      language: currentLangInfo?.ietfLanguageTag ?? currentLanguage,
      isRtl: currentLangInfo?.isRightToLeft ?? false,
      autoFillEnabled,
      themeName: savedThemeName,
    });
  };

  const settingsDialogTitleId = useId();

  return (
    <Modal
      isOpen={visible}
      titleAriaId={settingsDialogTitleId}
      onDismiss={onDiscard}
      styles={{
        main: {
          minHeight: 172,
          minWidth: 288,
          selectors: {
            [Utils.mobileQuery]: {
              minHeight: "30vh",
              minWidth: "unset",
            },
          },
        },
      }}
    >
      <Stack styles={{ root: { padding: 15 } }}>
        <Stack grow={0} styles={{ root: { marginBottom: 15 } }}>
          <Text variant="large" id={settingsDialogTitleId}>
            {ClientStrings.SettingsDialogTitle}
          </Text>
        </Stack>
        <Stack grow={2}>
          {languages.length ? <SettingsForm languages={languages} settings={settings} onSettingsChanged={onSettingsChanged} /> : <></>}
        </Stack>
        <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 8, padding: 10 }} grow={0}>
          <PrimaryButton
            onClick={(): void => void dispatch(saveSettingsDialog(settings))}
            styles={{ root: { borderWidth: 1, borderStyle: "solid" } }}
          >
            {ClientStrings.SettingsSaveButtonText}
          </PrimaryButton>
          <DefaultButton onClick={onDiscard}>{ClientStrings.SettingsDiscardButtonText}</DefaultButton>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default SettingsDialog;
